/**
 * App Features Package - Main Export
 *
 * This package provides application-level features that operate across
 * multiple domain packages in the Trip Planner monorepo.
 */

// Import feature modules
import {
  dataManagement,
  initDataManagementServices,
  ExportImportModal,
  TripExportModal,
  TripImportModal,
  pdfExportService,
  pageContextDetector,
  pdfLayoutEngine,
  itineraryExportService,
  tripExportService,
  tripImportService
} from '../data-management/src/index.js';
import { printSystem, initPrintSystemServices } from '../print-system/src';
import { systemHealth, initSystemHealthServices } from '../system-health/src';
import { helpAssistance, initHelpAssistanceServices, helpService, HelpModal } from '../help-assistance/src';
import { aiChat, initAiChatServices, ChatInterface } from '../ai-chat/src';
import {
  discussionForum,
  initDiscussionForumServices,
  DiscussionOverlay,
  ChatToggle,
  useDiscussionMessages,
  usePostMessage
} from '../discussion-forum/src/index.js';
import {
  travelAgent,
  initTravelAgentServices,
  TravelAgentPage,
  travelAgentService
} from '../travel-agent/src/index.js';

/**
 * Initialize all app features services
 * This function should be called during application startup
 */
export const initAppFeaturesServices = async () => {
  initDataManagementServices();
  initPrintSystemServices();
  await initHelpAssistanceServices();
  initSystemHealthServices();
  initAiChatServices();
  initDiscussionForumServices();
  initTravelAgentServices();
  console.log('All App Features services initialized');
};

/**
 * Main app features interface
 * Provides unified access to all application-level features
 */
export const appFeatures = {
  // Feature modules
  dataManagement,
  printSystem,
  helpAssistance,
  systemHealth,
  aiChat,
  discussionForum,

  // Convenience methods
  async exportTrip(tripId, userId, format = 'json', options = {}) {
    return dataManagement.exportTrip(tripId, userId, format, options);
  },

  async printTrip(tripId, userId, template = 'tripSummary', format = 'html', options = {}) {
    return printSystem.printTrip(tripId, userId, template, format, options);
  },

  async runHealthCheck(userId, tripId = null) {
    return systemHealth.runHealthScan(userId, tripId);
  },

  showHelp(context = 'general') {
    return helpAssistance.showHelp(context);
  },

  async startAiChat(userId, tripId, options = {}) {
    return aiChat.startConversation(userId, tripId, options);
  },

  async createTripForum(tripId, ownerId) {
    return discussionForum.createTripForum(tripId, ownerId);
  },

  // Travel agent
  travelAgent,

  // Feature availability checks
  isFeatureAvailable(featureName) {
    return ['dataManagement', 'printSystem', 'helpAssistance', 'systemHealth', 'aiChat', 'discussionForum', 'travelAgent'].includes(featureName);
  },

  getAvailableFeatures() {
    return [
      {
        id: 'dataManagement',
        name: 'Data Management',
        description: 'Export, import, and backup trip data',
        capabilities: ['export', 'import', 'backup', 'restore']
      },
      {
        id: 'printSystem',
        name: 'Print System',
        description: 'Print trip data in various formats',
        capabilities: ['templates', 'formats', 'preview', 'print']
      },
      {
        id: 'helpAssistance',
        name: 'Help & Assistance',
        description: 'Contextual help and user guidance',
        capabilities: ['help', 'tutorials', 'search', 'context']
      },
      {
        id: 'systemHealth',
        name: 'System Health',
        description: 'Data integrity and optimization',
        capabilities: ['health', 'conflicts', 'suggestions', 'validation']
      },
      {
        id: 'aiChat',
        name: 'AI Chat Assistant',
        description: 'Context-aware AI assistance for trip planning',
        capabilities: ['chat', 'context', 'models', 'history']
      },
      {
        id: 'discussionForum',
        name: 'Discussion Forum',
        description: 'Multi-user trip planning discussions',
        capabilities: ['threads', 'messages', 'collaboration', 'permissions']
      },
      {
        id: 'travelAgent',
        name: 'My Travel Agent',
        description: 'AI-powered personal travel assistant with persistent sessions',
        capabilities: ['chat', 'sessions', 'artifacts', 'webSearch', 'tripContext']
      }
    ];
  }
};

// Export individual features for direct access
export {
  dataManagement,
  printSystem,
  helpAssistance,
  helpService,
  HelpModal,
  systemHealth,
  aiChat,
  discussionForum,
  ExportImportModal,
  TripExportModal,
  TripImportModal,
  pdfExportService,
  pageContextDetector,
  pdfLayoutEngine,
  itineraryExportService,
  tripExportService,
  tripImportService,
  // AI Chat Components
  ChatInterface,
  // Discussion Forum Components (Phase 1.3)
  DiscussionOverlay,
  ChatToggle,
  // Discussion Forum Hooks (Phase 1.2)
  useDiscussionMessages,
  usePostMessage,
  // Travel Agent (Phase 1)
  TravelAgentPage,
  travelAgent,
  travelAgentService
};

// Export individual initialization functions
export {
  initDataManagementServices,
  initPrintSystemServices,
  initHelpAssistanceServices,
  initSystemHealthServices,
  initAiChatServices,
  initDiscussionForumServices,
  initTravelAgentServices
};

// Default export
export default appFeatures;